import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { AttrIcon, swatchColor } from "@/lib/icons";
import { Heart, ChevronLeft, ChevronRight, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import StyleAdvisor from "@/components/StyleAdvisor";
import ProductImageTile from "@/components/ProductImageTile";
import LooxsEmblem from "@/components/LooxsEmblem";
import PageMeta from "@/components/PageMeta";

// Inline waitlist form used inside the product-detail sidebar.
function ProductWaitlistInline({ product }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/waitlist", { email: email.trim(), product_slug: product.slug, product_name: product.name });
      setDone(true);
      toast.success("You're on the boutique waitlist");
    } catch {
      toast.error("Could not join waitlist. Please try again.");
    }
    setBusy(false);
  };
  if (done) return <p data-testid="pdp-waitlist-done" className="font-editorial text-lg text-ink">We will write to you the moment this piece returns.</p>;
  return (
    <form onSubmit={submit} className="flex gap-2 items-end">
      <label className="flex-1">
        <span className="text-[10px] tracking-widest-plus uppercase text-ink/50">Notify me by email</span>
        <input
          data-testid="pdp-waitlist-email"
          type="email" required value={email} onChange={(e)=>setEmail(e.target.value)}
          placeholder="your@email.com"
          className="w-full border-b border-ink/25 focus:border-goldDark bg-transparent py-2 text-sm outline-none"
        />
      </label>
      <button
        data-testid="pdp-waitlist-submit"
        disabled={busy}
        className="bg-ink text-ivory px-6 py-2 text-[10px] tracking-widest-plus uppercase hover:bg-goldDark transition-colors duration-500 disabled:opacity-40"
      >
        {busy ? "…" : "Notify me"}
      </button>
    </form>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [params, setParams] = useSearchParams();
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [thumbIndex, setThumbIndex] = useState(0);
  const [zoom, setZoom] = useState(null);
  const [preorderOpen, setPreorderOpen] = useState(false);
  const [wished, setWished] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setData(null); setThumbIndex(0);
    api.get(`/products/${slug}`).then(r => {
      setData(r.data);
      const p = r.data.product;
      const initialColor = params.get("color") || p.variants?.[0]?.color || "";
      setColor(initialColor);
      const initialSize = params.get("size") || p.variants?.find(v => v.color === initialColor)?.size || "";
      setSize(initialSize);
    }).catch(() => setData({ error: true }));
    // eslint-disable-next-line
  }, [slug]);

  const product = data?.product;

  // Gallery for the currently selected colour, falls back to product.images
  const gallery = useMemo(() => {
    if (!product) return [];
    const g = product.color_galleries?.[color];
    return (g && g.length > 0) ? g : (product.images || []);
  }, [product, color]);

  useEffect(() => { setThumbIndex(0); }, [color]);

  // Persist selected variant in URL
  const selectColor = (c) => {
    setColor(c);
    const next = new URLSearchParams(params);
    next.set("color", c);
    // If current size not available in this color, clear it
    const av = product?.variants.some(v => v.color === c && v.size === size);
    if (!av) {
      const first = product?.variants.find(v => v.color === c);
      if (first) { setSize(first.size); next.set("size", first.size); }
      else next.delete("size");
    }
    setParams(next, { replace: true });
  };
  const selectSize = (s) => {
    setSize(s);
    const next = new URLSearchParams(params); next.set("size", s);
    setParams(next, { replace: true });
  };

  if (!data) return <div className="pt-40 text-center text-ink/50 tracking-widest-plus text-xs uppercase">Loading…</div>;
  if (data.error) return <div className="pt-40 text-center">Product not found</div>;

  const sizes = [...new Set(product.variants.map(v => v.size))];
  const colors = [...new Set(product.variants.map(v => v.color))];
  const variantFor = (c, s) => product.variants.find(v => v.color === c && v.size === s);
  const currentVariant = variantFor(color, size);
  const inStock = !!currentVariant && currentVariant.stock > 0;
  const inStore = product.availability === "in_store";
  const preOrder = product.availability === "pre_order";

  const handleWish = async () => {
    if (!user) { toast.error("Sign in to save"); return; }
    try {
      if (wished) { await api.delete(`/wishlist/${product.id}`); setWished(false); }
      else { await api.post(`/wishlist/${product.id}`); setWished(true); toast.success("Saved"); }
    } catch { toast.error("Try again"); }
  };

  return (
    <div className="bg-ivory text-ink pt-24">
      <PageMeta
        title={product.name}
        description={(product.description || "").slice(0, 155)}
        image={product.images?.[0] ? `https://www.looxsfashion.com${product.images[0]}` : undefined}
        type="product"
      />
      {/* Breadcrumb / crumbs */}
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-6 flex items-center gap-2 text-[10px] tracking-widest-plus uppercase text-black/50">
        <Link to="/collections" className="link-draw">Collections</Link><span>/</span>
        {product.collection_slug && (<><Link to={`/collections/${product.collection_slug}`} className="link-draw">{product.collection_slug.replace(/-/g," ")}</Link><span>/</span></>)}
        <span className="text-ink">{product.name}</span>
      </div>

      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-0">
        {/* Thumbnails column (desktop) */}
        <div className="hidden md:flex md:col-span-1 flex-col gap-2 p-4">
          {gallery.map((g, i) => (
            <button key={`${g}-${i}`} data-testid={`thumb-${i}`} onClick={() => setThumbIndex(i)} aria-label={`View image ${i+1}`}
              className={`aspect-square border overflow-hidden ${thumbIndex === i ? "border-ink" : "border-black/15 hover:border-ink"} transition-colors`}>
              <img src={g} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>

        {/* Primary image */}
        <div className="md:col-span-7 relative">
          <div className="relative aspect-[4/5] bg-parchment overflow-hidden cursor-zoom-in" onClick={() => setZoom(gallery[thumbIndex])}>
            <img data-testid="primary-image" src={gallery[thumbIndex]} alt={product.name} className="w-full h-full object-cover transition-opacity duration-500" />
          </div>
          {gallery.length > 1 && (
            <div className="absolute inset-y-0 left-0 right-0 flex items-center justify-between px-4 pointer-events-none">
              <button data-testid="prev-image" onClick={(e) => { e.stopPropagation(); setThumbIndex((i) => (i - 1 + gallery.length) % gallery.length); }}
                className="pointer-events-auto bg-ivory/90 hover:bg-ink hover:text-ivory w-10 h-10 flex items-center justify-center" aria-label="Previous">
                <ChevronLeft className="w-4 h-4" strokeWidth={1} />
              </button>
              <button data-testid="next-image" onClick={(e) => { e.stopPropagation(); setThumbIndex((i) => (i + 1) % gallery.length); }}
                className="pointer-events-auto bg-ivory/90 hover:bg-ink hover:text-ivory w-10 h-10 flex items-center justify-center" aria-label="Next">
                <ChevronRight className="w-4 h-4" strokeWidth={1} />
              </button>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="md:col-span-4 p-8 md:p-12 md:sticky md:top-24 md:self-start">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-2">
            {product.collection_slug ? product.collection_slug.replace(/-/g," ") + " collection" : "LOOXS"} · Chapter {product.chapter_roman}
          </p>
          <h1 data-testid="product-name" className="font-editorial text-4xl md:text-5xl leading-[1.05] tracking-tight">{product.name}</h1>
          {product.tagline && <p className="mt-3 font-editorial-italic text-xl text-ink/70">{product.tagline}</p>}
          <div className="rule-gold w-16 my-6" />
          <div className="flex items-baseline justify-between">
            <p data-testid="product-availability" className="font-editorial text-2xl text-ink">
              {inStore ? "Available in boutique" : preOrder ? "Available by pre-order" : "Coming soon"}
            </p>
            <p className="text-[10px] tracking-widest-plus uppercase text-black/60">
              Enquire with concierge
            </p>
          </div>

          <p className="mt-8 leading-relaxed text-ink/80 font-light">{product.description}</p>

          {/* Inline boutique waitlist */}
          <div className="mt-10 border border-ink/15 p-6" style={{ background: "#f7efd9" }}>
            <p className="text-[10px] tracking-widest-plus uppercase text-goldDark mb-3">Boutique waitlist</p>
            <ProductWaitlistInline product={product} />
          </div>

          {/* Colour swatches */}
          <div className="mt-10">
            <p className="text-[10px] tracking-widest-plus uppercase text-black/50 mb-4">
              Colour · <span className="text-ink">{color}</span>
            </p>
            <div className="flex flex-wrap gap-3">
              {colors.map(c => (
                <button key={c} data-testid={`swatch-${c}`} onClick={() => selectColor(c)}
                  className={`flex items-center gap-2 pr-3 pl-1 py-1 border transition-colors ${color === c ? "border-ink" : "border-black/15 hover:border-ink"}`}>
                  <span className="w-6 h-6 rounded-full border border-black/20" style={{ backgroundColor: swatchColor(c) }} />
                  <span className="text-[10px] tracking-widest-plus uppercase">{c}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Size */}
          <div className="mt-8">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[10px] tracking-widest-plus uppercase text-black/50">Size</p>
              <button className="text-[10px] tracking-widest-plus uppercase underline">Size guide</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {sizes.map(s => {
                const v = variantFor(color, s);
                const oos = !v || v.stock <= 0;
                return (
                  <button key={s} data-testid={`size-${s}`} disabled={oos} onClick={() => selectSize(s)}
                    className={`min-w-[54px] px-3 py-2 border text-sm transition-colors relative ${size === s ? "border-ink bg-ink text-ivory" : oos ? "border-black/10 text-black/25 cursor-not-allowed" : "border-black/25 hover:border-ink"}`}>
                    {s}
                    {oos && <span className="absolute -bottom-4 left-0 right-0 text-[8px] tracking-widest uppercase text-black/40 whitespace-nowrap">Out of stock</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="mt-14 flex gap-3">
            {inStore ? (
              <Link to="/stores" data-testid="find-in-store-btn" className="flex-1 block text-center bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-gold hover:text-ink transition-colors duration-500">
                Find in boutique
              </Link>
            ) : preOrder ? (
              <button data-testid="preorder-btn" onClick={() => setPreorderOpen(true)}
                className="flex-1 bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-gold hover:text-ink transition-colors duration-500">
                Request pre-order
              </button>
            ) : (
              <button disabled className="flex-1 bg-ink/30 text-ivory py-4 text-[11px] tracking-widest-plus uppercase cursor-not-allowed">Coming soon</button>
            )}
            <button data-testid="wishlist-btn" onClick={handleWish} aria-label="Wishlist"
              className="w-14 border border-black/25 flex items-center justify-center hover:border-gold hover:text-gold transition-colors">
              <Heart className={`w-4 h-4 ${wished ? "fill-gold text-gold" : ""}`} strokeWidth={1.25} />
            </button>
          </div>
          <p className="mt-4 text-[10px] tracking-widest-plus uppercase text-black/40 text-center">
            Online sale is reserved for gift cards. Every LOOXS piece is fitted in-store or by appointment.
          </p>
          <StyleAdvisor product={product} size={size} color={color} />

          {/* Attribute icons */}
          <div className="mt-14 grid grid-cols-2 gap-x-6 gap-y-8">
            <Attr icon="fabric"    title="Premium fabric"  body="Crafted for lasting comfort and durability." />
            <Attr icon="tailoring" title="Tailored fit"     body="Designed for a refined silhouette." />
            <Attr icon="comfort"   title="All-day comfort"  body="Lightweight construction for movement." />
            <Attr icon="london"    title="Designed in London"body="Contemporary design rooted in London." />
          </div>

          {/* Detail accordions */}
          <div className="mt-14 space-y-0">
            {product.craftsmanship && (<Detail label="Craftsmanship" body={product.craftsmanship} />)}
            {product.fit && (<Detail label="Fit" body={product.fit} />)}
            {product.care && (<Detail label="Care" body={product.care} />)}
            <Detail label="Shipping & appointments" icon="shipping" body="LOOXS pieces are collected in-store or fitted by private appointment. Pre-orders are dispatched from London within 6–8 weeks." />
            <Detail label="Returns" icon="returns" body="Exchanges within 30 days when returned in original condition. Made-to-order pieces are final." />
          </div>
        </div>
      </div>

      {/* Chapter cinematic film — plays under the still images */}
      {data.chapter?.hero_video && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-16 border-t border-black/10 mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="text-[10px] tracking-luxury uppercase text-gold">Chapter {data.chapter.roman} · Film</p>
              <h2 className="font-editorial text-3xl md:text-5xl tracking-tight mt-1">{data.chapter.name}</h2>
              {data.chapter.tagline && <p className="font-editorial-italic text-xl text-ink/70 mt-2">{data.chapter.tagline}</p>}
            </div>
            <p className="text-[10px] tracking-widest-plus uppercase text-black/50 hidden md:block">A LOOXS film</p>
          </div>
          <div className="relative aspect-[16/9] bg-parchment overflow-hidden">
            <video
              data-testid="chapter-hero-video"
              src={data.chapter.hero_video}
              autoPlay muted loop playsInline
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </section>
      )}

      {/* SHOP THE COLOURS — interactive panels below */}
      {colors.length > 1 && (
        <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 border-t border-black/10 mt-16">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Shop the colours</p>
          <h2 className="font-editorial text-3xl md:text-5xl tracking-tight mb-12">Every tone, every angle.</h2>
          <div className="grid gap-8 md:gap-6" style={{ gridTemplateColumns: `repeat(${colors.length}, minmax(0, 1fr))` }}>
            {colors.map((c, i) => {
              const cg = product.color_galleries?.[c] || product.images || [];
              return (
                <div key={c} data-testid={`color-panel-${c}`} className={`relative animate-fade-up`} style={{ animationDelay: `${i*80}ms` }}>
                  <div className="mb-3 flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: swatchColor(c) }} />
                    <span className="text-[10px] tracking-widest-plus uppercase">{c}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {(cg.length > 1 ? cg : [cg[0], cg[0]]).slice(0, 3).map((img, j) => (
                      <button key={`${c}-${img}-${j}`} data-testid={`color-panel-${c}-img-${j}`}
                        onClick={() => { selectColor(c); setThumbIndex(Math.min(j, gallery.length-1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="aspect-[3/4] bg-parchment overflow-hidden group">
                        <img src={img} alt={`${product.name} — ${c}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </button>
                    ))}
                  </div>
                  <button onClick={() => selectColor(c)} className="mt-4 text-[10px] tracking-widest-plus uppercase link-draw">Select {c} →</button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Related */}
      {data.related?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-8 md:px-16 py-24">
          <p className="text-[10px] tracking-luxury uppercase text-gold mb-4">Continue</p>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight mb-12">Related pieces</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-16">
            {data.related.map((p, i) => (
              <Link key={p.id} to={`/product/${p.slug}`} className="group animate-fade-up" style={{ animationDelay: `${i*60}ms` }}>
                <ProductImageTile src={p.images[0]} hoverSrc={p.images[1]} alt={p.name} />
                <p className="mt-4 text-[10px] tracking-widest-plus uppercase text-gold">Chapter {p.chapter_roman}</p>
                <p className="font-editorial text-lg">{p.name}</p>
                <p className="text-[10px] tracking-widest-plus uppercase text-ink/50 mt-1">
                  {p.availability === "in_store" ? "In boutique" : p.availability === "pre_order" ? "Pre-order" : "Enquire"}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Zoom modal */}
      {zoom && (
        <div className="fixed inset-0 z-50 bg-ink/95 flex items-center justify-center p-4" onClick={() => setZoom(null)}>
          <button className="absolute top-6 right-6 text-ivory" onClick={() => setZoom(null)} aria-label="Close"><XIcon className="w-6 h-6" strokeWidth={1.25} /></button>
          <img src={zoom} alt="" className="max-h-[92vh] max-w-[95vw] object-contain" />
        </div>
      )}

      {preorderOpen && <PreorderModal product={product} size={size} color={color} onClose={() => setPreorderOpen(false)} />}
    </div>
  );
}

function Attr({ icon, title, body }) {
  return (
    <div className="flex gap-3">
      <div className="text-gold flex-shrink-0"><AttrIcon name={icon} size={26} /></div>
      <div>
        <p className="text-[10px] tracking-widest-plus uppercase mb-1">{title}</p>
        <p className="text-xs text-ink/60 leading-relaxed">{body}</p>
      </div>
    </div>
  );
}

function Detail({ label, body, icon }) {
  return (
    <details className="border-t border-black/10 py-4 group">
      <summary className="flex items-center justify-between cursor-pointer text-[11px] tracking-widest-plus uppercase list-none">
        <span className="flex items-center gap-3">{icon && <AttrIcon name={icon} size={18} />} {label}</span>
        <span className="text-gold transition-transform group-open:rotate-45">+</span>
      </summary>
      <p className="mt-4 text-sm leading-relaxed text-ink/70">{body}</p>
    </details>
  );
}

function PreorderModal({ product, size, color, onClose }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/preorders", { ...f, product_id: product.id, size, color });
      toast.success("Pre-order received. Our concierge will contact you.");
      onClose();
    } catch { toast.error("Please try again."); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70" onClick={onClose} />
      <div className="relative bg-ivory text-ink max-w-lg w-full p-10 animate-fade-up">
        <p className="text-[10px] tracking-luxury uppercase text-gold mb-3">Limited release</p>
        <h3 className="font-editorial text-3xl leading-tight">Pre-order {product.name}</h3>
        <p className="text-xs tracking-widest-plus uppercase text-black/50 mt-2">{size} · {color}</p>
        <div className="rule-gold w-16 my-6" />
        <p className="text-sm text-ink/70 leading-relaxed mb-6">A LOOXS advisor will contact you privately to confirm details and arrange a fitting.</p>
        <form onSubmit={submit} className="space-y-4">
          {[["name","Full name","text"],["email","Email","email"],["phone","Phone","tel"]].map(([k,l,t]) => (
            <label key={k} className="block">
              <span className="text-[10px] tracking-widest-plus uppercase text-black/50">{l}</span>
              <input required data-testid={`preorder-${k}`} type={t} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})}
                className="w-full border-b border-black/25 focus:border-gold py-2 outline-none bg-transparent" />
            </label>
          ))}
          <label className="block">
            <span className="text-[10px] tracking-widest-plus uppercase text-black/50">Notes (optional)</span>
            <textarea rows="2" value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})}
              className="w-full border-b border-black/25 focus:border-gold py-2 outline-none bg-transparent resize-none" />
          </label>
          <button data-testid="preorder-submit" disabled={busy} className="w-full bg-ink text-ivory py-4 text-[11px] tracking-widest-plus uppercase hover:bg-gold hover:text-ink transition-colors duration-500 disabled:opacity-40">
            {busy ? "Submitting…" : "Submit request"}
          </button>
        </form>
      </div>
    </div>
  );
}
